import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import MasterIntakeQuestion, { masterApplies } from "../components/MasterIntakeQuestion";
import { SignaturePad } from "../components/SignaturePad";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API_BASE = api.defaults.baseURL;

const PHI_CONSENT = [
  ["Why this authorization is necessary",
   "At Silver Home Care, we prioritize protecting your personal information, including any Protected Health Information (PHI) you provide to us. To deliver the best possible care and streamline our processes, we use Google Workspace, a HIPAA-compliant cloud platform, for secure data storage.\n\nThis system ensures that your information is handled in compliance with strict privacy and security standards, safeguarded under a Business Associate Agreement (BAA) with Google Workspace. Additionally, using electronic signatures allows us to efficiently and securely manage your forms and authorizations, in line with legal requirements."],
  ["Authorization terms",
   "1. Consent for PHI Storage: By completing this form and providing your information, you consent to Silver Home Care storing your data, including PHI, on this secure platform.\n\n2. Consent for Electronic Signatures: By signing this document electronically, you agree that your electronic signature is legally binding and carries the same validity as a physical signature. This process complies with the Electronic Signatures in Global and National Commerce Act (E-SIGN) and the Uniform Electronic Transactions Act (UETA).\n\n3. Opt-Out Instructions: If you do not consent, please exit this form and contact Silver Home Care at 800-719-6912. Do not complete or submit further forms."],
];

export default function PublicIntakePage() {
  const { token } = useParams();
  const [payload, setPayload] = useState(null);
  const [status, setStatus] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [laterOpen, setLaterOpen] = useState(false);
  const [laterEmail, setLaterEmail] = useState("");
  const [laterMsg, setLaterMsg] = useState("");
  const [medBusy, setMedBusy] = useState(false);
  const [sigName, setSigName] = useState("");
  const [sigAgree, setSigAgree] = useState(false);
  const [checkedDocs, setCheckedDocs] = useState({});
  const [agrInitials, setAgrInitials] = useState({});
  const [agrSig, setAgrSig] = useState("");
  const [cpSig, setCpSig] = useState("");
  const [signBusy, setSignBusy] = useState(false);

  const loadQuestions = async () => {
    const r = await api.get(`/public/master-intake/${token}`);
    setPayload(r.data);
    setAnswers(r.data.answers || {});
  };
  const loadStatus = async () => {
    const r = await api.get(`/intake/${token}`);
    setStatus(r.data);
  };

  useEffect(() => {
    (async () => {
      try {
        const [qr, sr] = await Promise.all([
          api.get(`/public/master-intake/${token}`),
          api.get(`/intake/${token}`),
        ]);
        setPayload(qr.data);
        setAnswers(qr.data.answers || {});
        setStatus(sr.data);
        const visible = (qr.data.questions || []).filter((question) => masterApplies(question, qr.data.answers || {}));
        if (qr.data.readiness?.agreement_ready && qr.data.readiness?.careplan_ready) setIdx(visible.length);
      } catch (e) {
        setError(formatApiError(e));
      }
    })();
  }, [token]);

  const questions = useMemo(() => (payload?.questions || []).filter((q) => masterApplies(q, answers)), [payload, answers]);
  const atSign = idx >= questions.length;
  const q = questions[idx];

  const saveAndGo = async (nextIdx) => {
    setSaving(true);
    try {
      const value = q ? answers[q.key] : null;
      const has = Array.isArray(value) ? value.length : value && typeof value === "object" ? Object.keys(value).length : String(value || "").trim();
      if (q && has) await api.put(`/public/master-intake/${token}`, { answers: { [q.key]: value } });
      await Promise.all([loadQuestions(), loadStatus()]);
      setIdx(nextIdx);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadMeds = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setMedBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/public/intake/${token}/upload-medication-list`, fd, { timeout: 120000 });
      setAnswers((a) => ({ ...a, q61: { ...(a.q61 || {}), value: "Current list attached" } }));
      toast.success("Medication list received");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setMedBusy(false);
    }
  };

  const finishLater = async (emailMe) => {
    try {
      await api.post(`/public/intake/${token}/finish-later`);
      if (emailMe) {
        if (!laterEmail.includes("@")) { toast.error("Enter your email first"); return; }
        await api.post(`/public/intake/${token}/email-link`, { email: laterEmail.trim() });
        setLaterMsg(`Link sent to ${laterEmail.trim()}.`);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setLaterMsg("Link copied — your answers are saved.");
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const signPhi = async () => {
    setSignBusy(true);
    try {
      await api.post(`/intake/${token}/sign-consent`, { signer_name: sigName.trim() });
      setSigAgree(false);
      await loadStatus();
      toast.success("Consent signed");
    } catch (e) { setError(formatApiError(e)); } finally { setSignBusy(false); }
  };

  const signAgreement = async () => {
    const secs = status?.agreement_doc?.sections || [];
    if (secs.some((s) => !(agrInitials[s.key] || "").trim())) { toast.error("Please initial every authorization section"); return; }
    if (!agrSig) { toast.error("Please draw your signature"); return; }
    setSignBusy(true);
    try {
      await api.post(`/intake/${token}/sign-agreement`, { initials: agrInitials, signer_name: sigName.trim(), signature_png: agrSig });
      await loadStatus();
      toast.success("Service Agreement signed");
    } catch (e) { setError(formatApiError(e)); } finally { setSignBusy(false); }
  };

  const signCareplan = async () => {
    if (!cpSig) { toast.error("Please draw your signature"); return; }
    setSignBusy(true);
    try {
      await api.post(`/intake/${token}/sign-careplan`, { signer_name: sigName.trim(), signature_png: cpSig });
      await loadStatus();
      toast.success("Care Plan signed");
    } catch (e) { setError(formatApiError(e)); } finally { setSignBusy(false); }
  };

  const signAll = async () => {
    const keys = (status?.acknowledgements || []).filter((a) => !a.signed && checkedDocs[a.key]).map((a) => a.key);
    if (!keys.length) { toast.error("Check each document you have read"); return; }
    setSignBusy(true);
    try {
      await api.post(`/intake/${token}/sign-all`, { signer_name: sigName.trim(), keys });
      setCheckedDocs({});
      await loadStatus();
      toast.success("Documents signed");
    } catch (e) { setError(formatApiError(e)); } finally { setSignBusy(false); }
  };

  if (error && !payload) {
    return <div className="flex min-h-screen items-center justify-center bg-[#faf9f7] p-6"><p className="max-w-md rounded-2xl border bg-white p-8 text-center text-sm text-stone-700" data-testid="mi-public-error">{error}</p></div>;
  }
  if (!payload || !status) return <div className="flex min-h-screen items-center justify-center bg-[#faf9f7] text-sm text-stone-400" data-testid="mi-public-loading">Loading…</div>;

  const shell = (children, testid) => (
    <div className="min-h-screen bg-[#faf9f7]" data-testid="master-public-intake">
      <div className="border-b border-stone-200 bg-white px-5 py-4">
        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <img src="/shc-logo.png" alt="Silver Home Care" className="h-10 w-auto" />
          <div className="flex-1">
            <p className="font-heading text-sm font-semibold text-stone-900">Welcome, {payload.first_name || "there"}</p>
            <p className="text-xs text-stone-500">Your answers save as you go.</p>
          </div>
          <p className="text-xs font-medium text-stone-400" data-testid="mi-public-progress">{Math.min(idx + 1, questions.length + 1)} of {questions.length + 1}</p>
        </div>
      </div>
      <div className="mx-auto max-w-3xl px-5 py-8">
        <div className="rounded-3xl border border-stone-200 bg-white p-7 shadow-sm" data-testid={testid}>{children}</div>
      </div>
      <div className="pb-8 text-center">
        <button type="button" onClick={() => setLaterOpen((o) => !o)} className="text-xs font-medium text-[#6A3E97] hover:underline" data-testid="mi-public-later">Need to stop? Finish later</button>
        {laterOpen && (
          <div className="mx-auto mt-3 max-w-sm rounded-xl border bg-white p-4 text-left shadow-sm" data-testid="mi-public-later-panel">
            <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => finishLater(false)} data-testid="mi-public-copy-link">Copy my link</Button></div>
            <div className="mt-2 flex gap-2">
              <input value={laterEmail} onChange={(e) => setLaterEmail(e.target.value)} placeholder="you@email.com" className="h-9 flex-1 rounded-lg border border-stone-300 px-3 text-sm" data-testid="mi-public-later-email" />
              <Button size="sm" onClick={() => finishLater(true)} data-testid="mi-public-send-link">Email it</Button>
            </div>
            {laterMsg && <p className="mt-2 text-xs font-medium text-[#2E7D32]" data-testid="mi-public-later-msg">{laterMsg}</p>}
          </div>
        )}
      </div>
    </div>
  );

  if (!atSign && q) {
    return shell(
      <>
        <MasterIntakeQuestion q={q} value={answers[q.key]} onChange={(v) => setAnswers((a) => ({ ...a, [q.key]: v }))} testidPrefix="mpi" />
        {(q.key === "q61" || q.key === "q62") && (
          <div className="mt-4 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4" data-testid="mpi-med-upload">
            <p className="text-sm text-stone-600">Have a current medication list or a photo? You can upload it instead of typing every medication.</p>
            <input type="file" accept="application/pdf,image/*" onChange={uploadMeds} disabled={medBusy} className="mt-3 text-sm" data-testid="mpi-med-file" />
            {medBusy && <p className="mt-2 text-xs text-stone-400">Uploading…</p>}
          </div>
        )}
        <div className="mt-7 flex items-center justify-between">
          <button type="button" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0 || saving} className="text-sm text-stone-400 hover:text-stone-700 disabled:opacity-0" data-testid="mpi-back">← Back</button>
          <div className="flex gap-3">
            <button type="button" onClick={() => saveAndGo(idx + 1)} disabled={saving} className="text-sm text-stone-400 hover:text-stone-700" data-testid="mpi-skip">Skip</button>
            <Button onClick={() => saveAndGo(idx + 1)} disabled={saving} className="rounded-full bg-[#4A235A] px-7 text-white" data-testid="mpi-next">{saving ? "Saving…" : idx === questions.length - 1 ? "Finish questions" : "Save & next"}</Button>
          </div>
        </div>
      </>, "mpi-question-card");
  }

  const acks = status.acknowledgements || [];
  const agrDoc = status.agreement_doc;
  const cpDoc = status.careplan_doc;
  const unsigned = acks.filter((a) => !a.signed);
  const allDone = !unsigned.length && (!agrDoc || agrDoc.signed) && (!cpDoc || cpDoc.signed) && !!status.consent_signed;

  return shell(
    <>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-stone-400">Last step</p>
      <h1 className="mt-2 font-heading text-3xl font-semibold text-stone-900" data-testid="mpi-sign-title">Review and sign your documents</h1>
      <p className="mt-3 text-sm leading-relaxed text-stone-600">Your answers have filled these documents. Type your legal name once, then review and sign each item.</p>
      <input value={sigName} onChange={(e) => setSigName(e.target.value)} placeholder="Type your full legal name" className="mt-5 h-12 w-full rounded-xl border border-stone-300 px-4" data-testid="mpi-sign-name" />

      {!status.consent_signed && (
        <div className="mt-5 rounded-2xl border border-stone-200 bg-stone-50 p-5" data-testid="mpi-phi-card">
          <h2 className="font-heading text-lg font-semibold">Authorization & Consent for PHI Storage and E-Signatures</h2>
          <div className="mt-3 max-h-64 space-y-4 overflow-y-auto rounded-xl border bg-white p-4">
            {PHI_CONSENT.map(([h, body]) => <div key={h}><p className="text-sm font-bold">{h}</p>{body.split("\n\n").map((p, i) => <p key={i} className="mt-2 text-xs leading-relaxed text-stone-600">{p}</p>)}</div>)}
          </div>
          <label className="mt-3 flex items-start gap-2 text-sm"><input type="checkbox" checked={sigAgree} onChange={(e) => setSigAgree(e.target.checked)} className="mt-1" data-testid="mpi-phi-agree" /> I have read and agree.</label>
          <Button onClick={signPhi} disabled={signBusy || !sigAgree || sigName.trim().length < 3} className="mt-4 rounded-full bg-[#4A235A] text-white" data-testid="mpi-phi-sign">Sign consent</Button>
        </div>
      )}

      {agrDoc && (
        <div className="mt-5 rounded-2xl border border-stone-200 p-5" data-testid="mpi-agreement-card">
          <div className="flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Service Agreement</h2><a href={`${API_BASE}/intake/${token}/agreement/pdf`} target="_blank" rel="noreferrer" className="text-sm text-[#6A3E97] underline" data-testid="mpi-agreement-view">View document</a></div>
          {agrDoc.signed ? <p className="mt-3 text-sm font-bold text-[#2E7D32]" data-testid="mpi-agreement-signed">Signed ✓</p> : (
            <>
              <div className="mt-3 space-y-3">{agrDoc.sections.map((s) => <div key={s.key} className="rounded-xl border bg-stone-50 p-3"><p className="text-sm font-bold">{s.title}</p><p className="mt-1 max-h-20 overflow-y-auto text-xs text-stone-600">{s.text}</p><input maxLength={4} value={agrInitials[s.key] || ""} onChange={(e) => setAgrInitials((m) => ({ ...m, [s.key]: e.target.value }))} placeholder="Initials" className="mt-2 h-9 w-24 rounded-lg border border-stone-300 text-center" data-testid={`mpi-initials-${s.key}`} /></div>)}</div>
              <div className="mt-3 rounded-xl border bg-white" data-testid="mpi-agreement-pad"><SignaturePad onChange={setAgrSig} /></div>
              <Button onClick={signAgreement} disabled={signBusy || !agrSig || sigName.trim().length < 3} className="mt-3 rounded-full bg-[#4A235A] text-white" data-testid="mpi-agreement-sign">Sign Service Agreement</Button>
            </>
          )}
        </div>
      )}

      {cpDoc && (
        <div className="mt-5 rounded-2xl border border-stone-200 p-5" data-testid="mpi-careplan-card">
          <div className="flex items-center justify-between"><h2 className="font-heading text-lg font-semibold">Care Plan</h2><a href={`${API_BASE}/intake/${token}/careplan.pdf`} target="_blank" rel="noreferrer" className="text-sm text-[#6A3E97] underline" data-testid="mpi-careplan-view">View document</a></div>
          {cpDoc.signed ? <p className="mt-3 text-sm font-bold text-[#2E7D32]" data-testid="mpi-careplan-signed">Signed ✓</p> : (
            <><div className="mt-3 rounded-xl border bg-white" data-testid="mpi-careplan-pad"><SignaturePad onChange={setCpSig} /></div><Button onClick={signCareplan} disabled={signBusy || !cpSig || sigName.trim().length < 3} className="mt-3 rounded-full bg-[#4A235A] text-white" data-testid="mpi-careplan-sign">Sign Care Plan</Button></>
          )}
        </div>
      )}

      <div className="mt-5 space-y-3" data-testid="mpi-acknowledgements">
        {acks.map((a) => (
          <div key={a.key} className="flex items-center justify-between gap-3 rounded-2xl border border-stone-200 p-4" data-testid={`mpi-doc-${a.key}`}>
            <div><p className="text-sm font-bold text-stone-800">{a.title}</p><a href={`${API_BASE}/intake/${token}/template/${a.key}`} target="_blank" rel="noreferrer" className="text-xs text-[#6A3E97] underline" data-testid={`mpi-view-${a.key}`}>View document</a></div>
            {a.signed ? <span className="text-xs font-bold text-[#2E7D32]">Signed ✓</span> : <label className="flex items-center gap-2 text-xs font-medium"><input type="checkbox" checked={!!checkedDocs[a.key]} onChange={(e) => setCheckedDocs((c) => ({ ...c, [a.key]: e.target.checked }))} data-testid={`mpi-check-${a.key}`} /> I read it</label>}
          </div>
        ))}
      </div>
      {unsigned.length > 0 && <Button onClick={signAll} disabled={signBusy || sigName.trim().length < 3} className="mt-4 rounded-full bg-[#4A235A] text-white" data-testid="mpi-sign-docs">Sign checked documents</Button>}
      {allDone && <p className="mt-5 rounded-xl bg-[#2E7D32]/10 p-4 text-sm font-semibold text-[#2E7D32]" data-testid="mpi-all-done">All set — your documents are signed.</p>}
      <button type="button" onClick={() => setIdx(0)} className="mt-5 text-sm text-stone-500 underline" data-testid="mpi-review-answers">Review or change answers</button>
    </>, "mpi-signing-card");
}
